import { create } from "zustand";
import { Network } from "../components/InterfaceInfo";
import { ApiData, LocationResponse, SectionData } from "../api-request/api-request";
import { DynamicInputParameters, LocationParameters, SchemaParams } from "../types/ApiData";
import { newId, ParamFilterState } from "../pages/home/heat-map/GlobalFilter";
import { devtools } from "zustand/middleware";

type SearchMapType = Record<
  string,
  { edgeMatch: boolean; coordMatches: Set<string> }
>;
export type NetworkLoaded = Record<
  string,
  any
>

export type SelectedSection = Record<string, any>

export interface InputParamState {
    network: Network,
    data: ApiAllState,
    sections : SelectedSection,
    notifications: Notifications,
    schemaParams: SchemaParams,
    searchText: string;
    searchMap: SearchMapType;
    isSearchActive: boolean;
    paramFilter: ParamFilterState,
    networkLoaded: NetworkLoaded,
    storedLocationParams: LocationParameters,
    storedLocationData: LocationResponse,
    setNetwork: (value: Network) => void,
    setSection: (value: SelectedSection | any) => void,
    setData : (data: ApiAllState | any, fromLocCalc? : boolean) => void,
    setNotifications: (notif : Notifications, read: boolean) => void,
    clearNotification: () => void
    setSchemaParams: (params:SchemaParams) => void
    setSearchText: (text: string) => void;
    setSearchMap: (map: SearchMapType) => void;
    activateSearch: (val: boolean) => void;
    clearSearch: () => void;
    setParamFilter: (param: ParamFilterState) => void;
    setNetworkLoaded: (networkId: string, type: boolean) => void;
    insertstoredLocParamWithData: (params : {[index: string]: DynamicInputParameters}, data: {[key : string]: SectionData}) => void;
    updateLocParams: (params : {[index: string]: DynamicInputParameters}, data: {[key : string]: SectionData}, dataResponse: ApiAllState | any, networkId: string, type: boolean) => void
    deleteStoredLocParams: (coordinateKey: string) => void,
    deleteAllStoredLocParams: (network_id: string) => void,
}

export const useInputParamState = create<InputParamState>()(
//    devtools(
    (set) => ({
        sections: [],
        data: { status: "none" },
        network: {},
        notifications: { no_notifications: 0, notifications: [], read: false },
        schemaParams: {} as SchemaParams,
        searchText: "",
        searchMap: {},
        isSearchActive: false,
        networkLoaded: {"hamburg-kiel" : false, "rail-center-hamburg": false, "benidorm-denia": false, "hora-malesov": false, "usti-brandys": false},
        paramFilter: { logic: "AND", groups: [ { id: newId(), logic: "AND", rows: [] }]},
        storedLocationParams: {},
        storedLocationData: {},
        setNetwork: (value) => {
            set((state) => ({ network: value }));
        },
        setNetworkLoaded: (networkId: string, type: boolean) => {
            set((state) => ({
                networkLoaded: {
                    ...state.networkLoaded,
                    [networkId]: type,
                },
            }));
        },
        setData: (data: ApiAllState | any, fromLocCalc = false) => {
            set((state) => ({
                data,
                sections: fromLocCalc ? state.sections : []
            }));
        },
        setSection: (value: SelectedSection) => {
            set((state) => ({ sections: value }));
        },
        setNotifications: async (notif: Notifications, read: boolean) => {
            if (read) {
                set((state) => {
                    return { notifications: { ...state.notifications, read: true } };
                })
            }
            else if (notif.notifications && notif.notifications.length > 0) {
                set((state) => {
                    const updatedNotifications = [
                        ...state.notifications.notifications,
                        ...notif.notifications
                    ];

                    const latestNotifications = updatedNotifications.slice(-10);

                    let no_notif = 0
                    if (state.notifications.read) no_notif = notif.notifications.length // set number for unread
                    else no_notif = state.notifications.no_notifications + notif.notifications.length
                    no_notif = no_notif > 10 ? 10 : no_notif

                    return {
                        notifications: {
                            ...state.notifications,
                            no_notifications: no_notif, // Update count
                            read: false,
                            notifications: latestNotifications, // Keep only latest 10
                        }
                    };
                });
            }
        },
        clearNotification: async () => {
            set((state) => {
                return { notifications: { no_notifications: 0, notifications: [], read: false } };
            })
        },
        setSchemaParams: (params: SchemaParams) => {
            set((state) => {
                return { schemaParams: params};
            })
        },
        setSearchText: (text) => set({ searchText: text }),
        setSearchMap: (map) =>
            set({ searchMap: map, isSearchActive: true }),
        activateSearch: (val: boolean) =>
            set({ isSearchActive: val }),
        clearSearch: () =>
            set({ searchMap: {}, isSearchActive: false, searchText: '' }),
        setParamFilter: (params: ParamFilterState) => {
            set((state) => {
                return { paramFilter: params};
            })
        },
        insertstoredLocParamWithData: (params: { [index: string]: DynamicInputParameters }, data: { [key: string]: SectionData }) => {
            set((state) => ({
                storedLocationData: { ...state.storedLocationData, ...data },
                storedLocationParams: { ...state.storedLocationParams, ...params }
            }))
         },
        updateLocParams: (params : {[index: string]: DynamicInputParameters}, data: {[key : string]: SectionData}, dataResponse: ApiAllState | any, networkId: string, type: boolean) => {
            set((state) => ({
                storedLocationData: { ...state.storedLocationData, ...data },
                storedLocationParams: { ...state.storedLocationParams, ...params },
                data: dataResponse,
                networkLoaded: {
                    ...state.networkLoaded,
                    [networkId]: type,
                },
            }))
        },
        deleteStoredLocParams: (coordinateKey: string) => {
            set((state) => {
                const models = state.storedLocationParams;
                delete models[coordinateKey];
                const results = state.storedLocationData;
                delete results[coordinateKey];
                return { storedLocationParams: models, storedLocationData: results }
            });
        },
        deleteAllStoredLocParams: (network_id: string) => {
            set((state) => {
                const storedLocationParams = Object.fromEntries(
                    Object.entries(state.storedLocationParams).filter(
                        ([_, value]) => value?.network_id !== network_id
                    )
                );

                const storedLocationData = Object.fromEntries(
                    Object.entries(state.storedLocationData).filter(
                        ([_, value]) => value?.network_id !== network_id
                    )
                );

                return {
                    storedLocationParams,
                    storedLocationData,
                };
            });
        }, 
    })
)
//)



export interface SuccessfullSectionState {
    status: string,
    data?: SectionData,
    message?: string,
}


export interface ApiAllState {
    status : string,
    response?: ApiData | any,
    errors?: any
}

export interface Notifications {
    no_notifications: number,
    read: boolean,
    notifications: {[index: string]: any}[],
}
